import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api, type Me } from '../api';
import { money } from '../form';
import { useI18n } from '../i18n';
import { PageHead, PanelShell, PlanLock } from '../layout';
import { ListFilters, ListSummary, listQuery, type HubTab, type HubView, type ListSearch } from './filters';
import { useProjects } from './catalog';
import { IncomingList } from './incoming';
import { InboxBox } from './inbox';
import { OutgoingList } from './invoices';

interface Summary {
  receivable: number; receivableOverdue: number; payable: number; payableOverdue: number; inboxNew: number;
  counts: { outgoing: Record<'unpaid' | 'paid' | 'draft' | 'credit' | 'proforma' | 'all', number>; incoming: Record<'unpaid' | 'paid' | 'credit' | 'all', number>; all?: Record<'unpaid' | 'paid' | 'credit' | 'all', number> };
}
interface VatMonth { month: string; vatPayer: boolean; salesNet: number; salesVat: number; purchaseNet: number; purchaseVat: number; purchaseVatCost: number; toPay: number }

const OUT_VIEWS: [HubView, string][] = [['all', 'Visos'], ['unpaid', 'Neapmokėtos'], ['paid', 'Apmokėtos'], ['draft', 'Juodraščiai'], ['credit', 'Kreditinės'], ['proforma', 'Išankstinės']];
const IN_VIEWS: [HubView, string][] = [['all', 'Visos'], ['unpaid', 'Neapmokėtos'], ['paid', 'Apmokėtos'], ['credit', 'Kreditinės']];
const ALL_VIEWS: [HubView, string][] = [['all', 'Visos'], ['unpaid', 'Neapmokėtos'], ['paid', 'Apmokėtos'], ['credit', 'Kreditinės']];

/**
 * Sąskaitos — every invoice in one place: issued (money in) and received (money out), each split into what is
 * unpaid and what is paid; documents that came by e-mail; and VAT by month. The cards on top answer "who owes
 * what" at a glance.
 */
export function InvoicesPage({ me, search, onSearch }: { me: Me; search: ListSearch; onSearch: (s: ListSearch) => void }) {
  const { t } = useI18n();
  // Invoices by e-mail (read with AI) are Premium's.
  const premium = me.subscription?.level !== 'free' && me.subscription?.level !== 'basic';
  const tab: HubTab = search.tab ?? 'all';
  const summary = useQuery({ queryKey: ['invoice-summary'], queryFn: () => api.get<Summary>('api/v1/invoices/summary') });
  const s = summary.data;
  // A new tab or list starts without the previous list's filters (except the period).
  const go = (patch: ListSearch) => onSearch({ ...(search.from ? { from: search.from } : {}), ...(search.to ? { to: search.to } : {}), ...patch });
  const views = tab === 'in' ? IN_VIEWS : tab === 'all' ? ALL_VIEWS : OUT_VIEWS;
  const counts = (tab === 'in' ? s?.counts.incoming : tab === 'all' ? s?.counts.all : s?.counts.outgoing) as Record<string, number> | undefined;
  const view = search.view ?? 'all';
  const action = tab === 'in' || tab === 'inbox'
    ? <Link className="button button--primary" to="/incoming/new">+ {t('Pridėti gautą sąskaitą')}</Link>
    : <Link className="button button--primary" to="/outgoing/new">+ {t('Nauja sąskaita')}</Link>;
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Dokumentai')} title={t('Sąskaitos')} text={t('Išrašytos ir gautos sąskaitos vienoje vietoje: kas apmokėta, kas dar ne.')} action={action} />}>
      <section className="stat-grid hub-cards">
        <button type="button" className="hub-card" onClick={() => go({ tab: 'out', view: 'unpaid' })}>
          <span>{t('Mums turi sumokėti')}</span><strong>{money(s?.receivable ?? 0)}</strong><small>{t('išrašytos, neapmokėtos')}</small>
        </button>
        <button type="button" className={`hub-card${s?.receivableOverdue ? ' stat--alert' : ''}`} onClick={() => go({ tab: 'out', view: 'unpaid' })}>
          <span>{t('Iš jų vėluoja')}</span><strong>{money(s?.receivableOverdue ?? 0)}</strong><small>{t('praėjo apmokėjimo terminas')}</small>
        </button>
        <button type="button" className="hub-card" onClick={() => go({ tab: 'in', view: 'unpaid' })}>
          <span>{t('Mes turime sumokėti')}</span><strong>{money(s?.payable ?? 0)}</strong><small>{t('gautos, neapmokėtos')}</small>
        </button>
        <button type="button" className={`hub-card${s?.payableOverdue ? ' stat--alert' : ''}`} onClick={() => go({ tab: 'in', view: 'unpaid' })}>
          <span>{t('Iš jų vėluoja')}</span><strong>{money(s?.payableOverdue ?? 0)}</strong><small>{t('praėjo apmokėjimo terminas')}</small>
        </button>
      </section>

      <nav className="project-tabs page-tabs hub-tabs">
        {([['all', 'Visos sąskaitos', null], ['out', 'Išrašytos', null], ['in', 'Gautos', null], ['inbox', 'Gauta el. paštu', s?.inboxNew], ['vat', 'PVM', null]] as [HubTab, string, number | null | undefined][]).map(([key, name, badge]) => (
          <button key={key} type="button" className={tab === key ? 'active' : ''} onClick={() => go({ tab: key })}>
            {t(name)}{badge && !(key === 'inbox' && !premium) ? <span className="hub-badge">{badge}</span> : null}
            {key === 'inbox' && !premium ? <span className="section-lock">Premium</span> : null}
          </button>
        ))}
      </nav>

      {tab === 'out' || tab === 'in' || tab === 'all' ? (
        <nav className="hub-views">
          {views.map(([key, name]) => (
            <button key={key} type="button" className={view === key ? 'active' : ''} onClick={() => onSearch({ ...search, view: key })}>
              {t(name)}{counts?.[key] !== undefined ? <small>{counts[key]}</small> : null}
            </button>
          ))}
        </nav>
      ) : null}

      {tab === 'all' ? <AllList search={search} onSearch={onSearch} /> : null}
      {tab === 'out' ? <OutgoingList me={me} search={search} onSearch={onSearch} /> : null}
      {tab === 'in' ? <IncomingList search={search} onSearch={onSearch} premium={premium} /> : null}
      {tab === 'inbox' ? (premium ? <InboxBox open /> : <PlanLock me={me} name={t('Gauta el. paštu')} level="premium" />) : null}
      {tab === 'vat' ? <VatTab year={search.year ?? new Date().getFullYear()} onYear={(year) => onSearch({ ...search, year })} vatPayer={me.company.isVatPayer ?? false} /> : null}
    </PanelShell>
  );
}

function VatTab({ year, onYear, vatPayer }: { year: number; onYear: (y: number) => void; vatPayer: boolean }) {
  const { t } = useI18n();
  const data = useQuery({ queryKey: ['invoice-vat', year], queryFn: () => api.get<{ months: VatMonth[]; vatPayerFrom: string | null }>(`api/v1/invoices/vat?year=${year}`) });
  const now = new Date().toISOString().slice(0, 7);
  const months = (data.data?.months ?? []).filter((m) => m.month <= now);
  const sum = (f: (m: VatMonth) => number) => months.reduce((s, m) => s + f(m), 0);
  return (
    <section className="panel-card">
      <div className="card-head">
        <div><p className="eyebrow">{t('PVM suvestinė')}</p><h2>{year}</h2></div>
        <div className="hub-year">
          <button type="button" className="button button--ghost button--small" onClick={() => onYear(year - 1)}>←</button>
          <button type="button" className="button button--ghost button--small" disabled={year >= new Date().getFullYear()} onClick={() => onYear(year + 1)}>→</button>
        </div>
      </div>
      {!vatPayer ? <p className="muted">{t('Įmonė nėra PVM mokėtoja: tiekėjų sąskaitose sumokėtas PVM yra išlaida, jo atskaityti negalima. Tapus PVM mokėtoja (Įmonė → PVM mokėtojas nuo), PVM bus skaičiuojamas nuo tos dienos.')}</p> : null}
      {vatPayer && data.data?.vatPayerFrom ? <p className="muted">{t('PVM mokėtoja nuo {date}; ankstesnių sąskaitų PVM — išlaida.', { date: data.data.vatPayerFrom })}</p> : null}
      <p className="muted">{t('Pagal sąskaitų datas: PVM iš išrašytų sąskaitų (ir Z ataskaitų) minus PVM iš gautų. Tai apytikslė suma — deklaraciją tikrina buhalteris.')}</p>
      <div className="table-wrap">
        <table className="invoice-table">
          <thead><tr><th>{t('Mėnuo')}</th><th className="right">{t('Pardavimai be PVM')}</th><th className="right">{t('Pardavimų PVM')}</th><th className="right">{t('Pirkimai be PVM')}</th><th className="right">{t('Pirkimų PVM')}</th><th className="right">{t('Sumokėtas PVM (išlaida)')}</th><th className="right">{t('Mokėtinas PVM')}</th></tr></thead>
          <tbody>
            {months.map((m) => (
              <tr key={m.month}>
                <td>{m.month}</td>
                <td className="right">{money(m.salesNet)}</td><td className="right">{money(m.salesVat)}</td>
                <td className="right">{money(m.purchaseNet)}</td><td className="right">{money(m.purchaseVat)}</td><td className="right">{m.purchaseVatCost ? money(m.purchaseVatCost) : '—'}</td>
                <td className="right">{m.vatPayer ? <strong className={m.toPay < 0 ? 'hub-refund' : undefined}>{money(m.toPay)}</strong> : <span className="muted" title={t('Ne PVM mokėtojas')}>—</span>}</td>
              </tr>
            ))}
            {months.length ? (
              <tr className="recon-sum">
                <td>{t('Iš viso')}</td>
                <td className="right">{money(sum((m) => m.salesNet))}</td><td className="right">{money(sum((m) => m.salesVat))}</td>
                <td className="right">{money(sum((m) => m.purchaseNet))}</td><td className="right">{money(sum((m) => m.purchaseVat))}</td><td className="right">{money(sum((m) => m.purchaseVatCost))}</td>
                <td className="right">{money(sum((m) => m.toPay))}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <p className="muted">{t('Neigiama suma — PVM permoka (grąžintinas). Terminai ir mokėjimai — skiltyje')} <Link to="/taxes">{t('Mokesčiai')}</Link>.</p>
    </section>
  );
}

interface AllRow { id: number; direction: 'outgoing' | 'incoming'; kind: string; status: string; series: string; number: string; issueDate: string; dueDate: string | null;
  total: number; currency: string; buyerName: string; sellerName: string; overdue: boolean; left: number; paidAt: string | null }
const ALL_STATUS: Record<string, string> = { draft: 'Juodraštis', sent: 'Išsiųsta', paid: 'Apmokėta', cancelled: 'Atšaukta' };

/** Visos sąskaitos: issued and received together, newest first (the unpaid ones by due date); each opens where it is kept. */
function AllList({ search, onSearch }: { search: ListSearch; onSearch: (s: ListSearch) => void }) {
  const { t } = useI18n();
  const view = search.view ?? 'all';
  const query = listQuery('all', { ...search, view });
  const projects = useProjects();
  const list = useQuery({ queryKey: ['invoices', query], queryFn: () => api.get<{ invoices: AllRow[] }>(`api/v1/invoices?${query}`).then((r) => r.invoices) });
  const rows = list.data ?? [];
  // In the same split layout as the issued and received lists (one column), so switching tabs moves nothing.
  return (
    <div className="two-cols two-cols--invoice two-cols--single">
    <section className="panel-card invoice-list-panel">
      <ListFilters search={search} onSearch={onSearch} projects={projects.data ?? []} placeholder={t('Paieška: numeris, pirkėjas arba tiekėjas')} statuses={[]} />
      <div className="table-wrap"><table className="invoice-table">
        <thead><tr><th>{t('Nr.')}</th><th>{t('Partneris')}</th><th>{t('Data')}</th><th>{t('Būsena')}</th><th className="right">{t('Suma')}</th><th /></tr></thead>
        <tbody>
          {list.isSuccess && !rows.length ? <tr><td colSpan={6} className="empty">{t('Įrašų nėra.')}</td></tr> : null}
          {rows.map((r) => {
            const out = r.direction === 'outgoing';
            const state = r.overdue ? 'Vėluoja' : !out && r.status === 'draft' ? 'Laukiama' : ALL_STATUS[r.status] ?? r.status;
            return (
              <tr key={r.id} className={r.overdue ? 'is-overdue' : undefined}>
                <td>
                  <Link className="row-title" to={out ? '/outgoing/$id' : '/incoming/$id'} params={{ id: String(r.id) }}><strong>{`${r.series} ${r.number}`.trim()}</strong></Link>
                  <small>{out ? t('Išrašyta') : t('Gauta')}{r.kind === 'credit' ? ` · ${t('Kreditinė')}` : r.kind === 'proforma' ? ` · ${t('Išankstinė')}` : ''}</small>
                </td>
                <td>{out ? r.buyerName : r.sellerName}</td>
                <td>{r.issueDate}{r.status !== 'paid' && r.dueDate ? <small>{t('Apmokėti iki')} {r.dueDate}</small> : r.paidAt ? <small>{t('Apmokėta')} {r.paidAt.slice(0, 10)}</small> : null}</td>
                <td><span className={`badge badge--${r.overdue ? 'overdue' : r.status === 'paid' ? 'paid' : r.status === 'cancelled' ? 'cancelled' : out && r.status === 'sent' ? 'sent' : 'draft'}`}>{t(state)}</span></td>
                <td className="right"><strong>{money(out ? r.total : -r.total, r.currency)}</strong>{r.status !== 'paid' && r.left && r.left !== r.total ? <small>{t('liko {amount}', { amount: money(r.left, r.currency) })}</small> : null}</td>
                <td className="row-actions">
                  {out ? <Link to="/outgoing/$id" params={{ id: String(r.id) }}>{r.status === 'draft' ? t('Redaguoti') : t('Peržiūrėti')}</Link>
                    : <Link to="/incoming/$id" params={{ id: String(r.id) }}>{t('Peržiūrėti')}</Link>}
                  {out ? <Link to="/view" search={{ type: 'invoice', id: r.id, title: `${r.series} ${r.number}`.trim() }}>PDF</Link>
                    : <Link to="/view" search={{ type: 'file', id: r.id, title: `${r.series} ${r.number}`.trim() }}>{t('Failas')}</Link>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table></div>
      <ListSummary rows={view === 'unpaid' ? rows.map((r) => ({ total: r.direction === 'outgoing' ? r.left : -r.left })) : rows.map((r) => ({ total: r.direction === 'outgoing' ? r.total : -r.total }))} />
    </section>
    </div>
  );
}
